<template>
    <div class="page container t-align-center" data-page="eats">

        <div class="titleArea">
            <h1 class="title">Eats</h1>
            <p class="labelDesc">Order delicious Mongolian food made by Mongolians. Currently only in BC.</p>

            <nav class="tabLinks">
                <a href="#0" @click.prevent="toggleTabs('newOrder')" :class="{active: tab === 'newOrder'}">New Order</a>
                <a href="#0" @click.prevent="toggleTabs('orders')" :class="{active: tab === 'orders'}">Existing Orders</a>
            </nav>
            
            <span v-if="pageAllowed && tab === 'newOrder'" class="cart">
                Cart: ${{ cart.total }} + ${{ cart.delivery_fee }} delivery
            </span>
        </div>

        <!-- Orders -->
        <div v-if="orders !== null && tab === 'orders'" class="orders">
            <div v-for="(order, index) in orders" :key="order.id" class="orderItem">
                <h3>{{ index + 1 }}. Order for {{ order.userfirstName }} | 
                    <span class="badge"
                        :class="{
                            yellow: order.status === 'Preparing',
                            green: order.status === 'Delivered',
                            red: order.status === 'Pick Up'
                        }"
                    >{{ order.status }}</span>
                </h3>
                <h4>
                    <span v-if="order.buuz > 0">{{ order.buuz }} Buuz</span> 
                    <span v-if="order.bansh > 0">{{ order.bansh }} Bansh</span> 
                    <span v-if="order.guril > 0">{{ order.guril }} Guril</span>
                    | <span>Total: ${{ order.total }}</span>
                    
                </h4>
                <p>Delivery at: {{ order.time | removeUnderscores }} | {{ order.timestamp.toDate() | moment('MMM DD, YYYY') }}</p>
                <p>Order Id: {{ order.id }}</p>

                <div v-if="order.status === 'Pick Up'" class="infoAlerts t-align-center error">
                    <p class="info">
                        <ion-icon name="information-circle"></ion-icon> 
                        Failed to deliver the food. Please pick up at: #701 - 1150 Jervis Street, Vancouver, BC V6E 2C8 | (604) 999-8016
                    </p>
                </div>

                <div v-if="userStatus === 'admin'" class="orderActionBtns">
                    <a href="#0" v-if="order.status !== 'Preparing'" @click.prevent="changeOrderStatus(order.id, 'Preparing')" class="btn green">Preparing</a>
                    <a href="#0" v-if="order.status !== 'Delivered'" @click.prevent="changeOrderStatus(order.id, 'Delivered')" class="btn green">Delivered</a>
                    <a href="#0" v-if="order.status !== 'Pick Up'" @click.prevent="changeOrderStatus(order.id, 'Pick Up')" class="btn red">Pick Up</a>
                </div>
            </div>
        </div>

        <div v-if="pageAllowed && tab === 'newOrder'" class="centerArea">

            <!-- Step 1 -->
            <div v-if="page === 1">
                <h3 class="om-label">1. Select your food</h3>
                <div class="pure-g">
                    
                    <div v-for="(food, index) in foods" :key="index" class="pure-u-1 pure-u-lg-1-3 foodItem">
                        <div class="inner">
                            <div class="image foodImage" :style="{backgroundImage: 'url('+ food.image +')'}"></div>
                            <h3>{{ food.title }}</h3>
                            <h4>${{ food.price }} / {{ food.amount }}{{ food.unit }}</h4>
                            <p class="desc">{{ food.description }}</p>
                            <div class="pure-g itemCounter form">
                                <div class="pure-u-1-3">
                                    <a href="#0" class="btn green minusBtn" @click.prevent="decrease(food.id)">-</a>
                                </div>
                                <div class="pure-u-1-3">
                                    <input type="number" class="countInput" v-model="cart[food.id]" @change.prevent="getCartTotal" disabled="disabled" />
                                </div>
                                <div class="pure-u-1-3">
                                    <a href="#0" class="btn green addBtn" @click.prevent="increase(food.id)">+</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="isLoggedIn" class="pure-u-1">
                        <div v-show="cart.total > 0" class="actionBtns">
                            <a href="#0" class="btn green" @click.prevent="goTo(2)">Next</a>
                        </div>
                    </div>
                    <div v-else class="pure-u-1">
                        <div class="infoAlerts t-align-center">
                            <p class="info"><ion-icon name="information-circle"></ion-icon> 
                                Please login first, You do not need to be a member to order food.
                                <router-link to="/login" class="btn green">Login</router-link> or
                                <router-link to="/register" class="btn green">Register</router-link>
                            </p>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Step 2 -->
            <div v-if="page === 2">
                <div class="block">
                    <ion-icon name="navigate"></ion-icon>
                    <h3 class="om-label">2. Your delivery address</h3>
                    <p class="labelDesc">We currently deliver only in British Columbia (BC) at the moment.</p>
                    <div class="form pure-g addressForm">
                        <div class="pure-u-1 pure-u-lg-12-24">
                            <input 
                                type="text"
                                name="street"
                                v-model="cart.address.street"
                                placeholder="Street"
                                :class="{error: cart.address.street === ''}"
                                @change.prevent="checkAddress" />
                        </div>
                        <div class="pure-u-1 pure-u-lg-6-24">
                            <input
                                type="text"
                                name="city"
                                v-model="cart.address.city"
                                placeholder="City"
                                :class="{error: cart.address.city === ''}"
                                @change.prevent="checkAddress" />
                        </div>
                        <div class="pure-u-1 pure-u-lg-2-24">
                            <input
                                type="text"
                                name="state"
                                v-model="cart.address.state"
                                placeholder="State"
                                :class="{error: cart.address.state === ''}"
                                disabled="disabled" />
                        </div>
                        <div class="pure-u-1 pure-u-lg-4-24">
                            <input
                                type="text"
                                name="postalCode"
                                v-model="cart.address.postalCode"
                                placeholder="Postal code"
                                :class="{error: cart.address.postalCode === ''}"
                                @change.prevent="checkAddress"
                                minlength="6"
                                maxlength="6" />
                        </div>
                    </div>
                </div>
                <div v-show="cart.total > 0" class="actionBtns pure-u-1">
                    <a href="#0" class="btn secondary" @click.prevent="goTo(1)">Back</a>
                    <a v-if="cart.addressValid" href="#0" class="btn green" @click.prevent="goTo(3)">Next</a>
                </div>
            </div>

            <!-- Step 3 -->
            <div v-if="page === 3">
                <div class="block">
                    <ion-icon name="phone-portrait"></ion-icon>
                    <h3 class="om-label">3. Your phone number</h3>
                    <p class="labelDesc">Add your phone number so we can contact you directly when our drivers can't find you.</p>
                    <div class="form phoneNumberForm">
                        <input
                            type="tel"
                            v-model="cart.phoneNumber"
                            placeholder="Phone Number"
                            :class="{error: cart.phoneNumber === ''}"
                            @change.prevent="checkPhoneNumber"
                            minlength="10"
                            maxlength="10" />
                    </div>
                </div>
                <div v-show="cart.total > 0" class="actionBtns pure-u-1">
                    <a href="#0" class="btn secondary" @click.prevent="goTo(2)">Back</a>
                    <a v-if="cart.phoneNumberValid" href="#0" class="btn green" @click.prevent="goTo(4)">Next</a>
                </div>
            </div>

            <!-- Step 4 -->
            <div v-if="page === 4">
                <div class="block large">
                    <ion-icon name="time"></ion-icon>
                    <div v-if="times.length > 0">
                        <h3 class="om-label">4. Select Your Delivery Time</h3>
                        <p class="labelDesc">Please select your time so we can bring your food at your preferred time.</p>

                        <div class="form timeForm">
                            <select v-model="cart.time">
                                <option value="">-- Select Time --</option>
                                <option v-for="(time, index) in times" :key="index" :value="time.id">{{ time.text }}</option>
                            </select>
                        </div>
                    </div>
                    <div v-else>
                        <h3 class="om-label">4. Delivery Closed.</h3>
                        <p class="labelDesc">Our delivery is currently closed. You can order tomorrow from: 10:00 am to 10:00 pm</p>
                    </div>
                </div>
                <div v-show="cart.total > 0" class="actionBtns pure-u-1">
                    <a href="#0" class="btn secondary" @click.prevent="goTo(3)">Back</a>
                    <a v-if="cart.time !== ''" href="#0" class="btn green" @click.prevent="goTo(5)">Next</a>
                </div>
            </div>

            <!-- Step 5 -->
            <div v-if="page === 5">
                <div class="block">
                    <h3 class="om-label">5. Delivery Fee</h3>
                    <h2 class="fee">${{ cart.delivery_fee }}</h2>

                    <p v-if="cart.delivery_fee === 0" class="labelDesc">Your deliver fee is now Free.</p>
                    <p v-else class="labelDesc">$5 minimim order delivery fee applied. <br> or Free delivery fee on Orders over or equal to $75.</p>
                    
                    <a href="#0" v-if="cart.delivery_fee === 5" @click.prevent="goTo(1)" class="btn green">Add more items to cart</a>
                </div>
                <div v-show="cart.total > 0" class="actionBtns pure-u-1">
                    <a href="#0" class="btn secondary" @click.prevent="goTo(4)">Back</a>
                    <a v-if="cart.delivery !== null" href="#0" class="btn green" @click.prevent="goTo(6)">Next</a>
                </div>
            </div>

            <!-- Step 6 -->
            <div v-if="page === 6">
                <div class="block">
                    <ion-icon name="card"></ion-icon>
                    <h3 class="om-label">6. Payment</h3>
                    <p class="labelDesc">Total ${{ cart.total + cart.delivery_fee }} will be charged now.</p>

                    <div class="paymentForm">
                        <Payment
                            v-if="payment.loadPayment"
                            :type="payment.type"
                            :eventId="payment.id"
                            :amount="payment.price"
                            :amountText="payment.priceText"
                            :label="payment.label"
                            @paymentSuccess="paymentSuccess($event)"
                        ></Payment>
                    </div>
                </div>
                <div v-show="cart.total > 0" class="actionBtns pure-u-1">
                    <a href="#0" class="btn secondary" @click.prevent="goTo(4)">Back</a>
                </div>
            </div>

            <!-- Step 7-->
            <div v-if="page === 7">
                <div class="block">
                    <ion-icon name="checkmark-circle"></ion-icon>
                    <h2 class="om-label">Your payment has been successfully completed.</h2>
                    <h4>Your food will be delivered between {{ cart.time | removeUnderscores }}.</h4>
                    <h4 v-if="cart.orderId">Order id: {{ cart.orderId }}</h4>
                </div>
            </div>

        </div>
        <div v-else-if="!pageAllowed && tab === 'newOrder'" class="centerArea">
            <div class="block">
                <ion-icon name="navigate"></ion-icon>
                <h2 class="om-label">Currently Eats is not available in {{ this.$store.state.location.city }}.</h2>
                <p class="labelDesc">Contact us at here <a href="mailto:info@onemgl.com" class="btn secondary">info@onemgl.com</a> if you would like start making food in your area.</p>
            </div>
        </div>

    </div>
</template>

<script>
    const fb = require('../../firebaseConfig.js')
    import moment from 'moment'
    import Payment from '../../components/Payment.vue'

    export default {
        components: {
            Payment
        },
        data(){
            return{
                tab: 'newOrder',
                page: 1,
                pageAllowed: true,
                locationAllowed: false,
                userProfile: null,
                isLoggedIn: false,
                foods: [
                    {
                        id: 'buuz',
                        title: 'Buuz',
                        description: 'A delicious frozen buuz ready to be cooked and served to your dinner table.',
                        price: 25,
                        amount: 1,
                        unit: 'kg',
                        image: 'https://images.prismic.io/vanmglcom/d713b9a6-e70d-4564-a229-5d8ff55322c4_buuz.jpg?auto=compress,format'
                    },
                    {
                        id: 'bansh',
                        title: 'Bansh',
                        description: 'A delicious frozen bansh ready to be cooked in your soup or milk tea.',
                        price: 20,
                        amount: 1,
                        unit: 'kg',
                        image: 'https://images.prismic.io/vanmglcom/98426a19-d329-4b70-b3d8-61236bc79073_bansh.jpg?auto=compress,format'
                    },
                    {
                        id: 'guril',
                        title: 'Guril',
                        description: 'Your ready to cook frozen tsuivan guril is ready to be cooked.',
                        price: 10,
                        amount: 1,
                        unit: 'kg',
                        image: 'https://images.prismic.io/vanmglcom/6218776d-0649-4b72-b8e5-b9cefe068a31_guril.jpg?auto=compress,format'
                    }
                ],
                cart: {
                    buuz: 0,
                    buuz_unit_price: 25,
                    bansh: 0,
                    bansh_unit_price: 20,
                    guril: 0,
                    guril_unit_price: 10,
                    total: 0.00,
                    address: {
                        street: '',
                        city: '',
                        postalCode: '',
                        state: 'BC',
                        country: 'CA'
                    },
                    addressValid: false,
                    phoneNumber: '',
                    phoneNumberValid: false,
                    time: '',
                    userId: '',
                    userFirstName: '',
                    userPhotoURL: '',
                    timestamp: null,
                    orderId: null,
                    status: '',
                    delivery: '$5 minimum delivery fee.',
                    delivery_fee: 5
                },
                payment: {
                    loadPayment: false,
                    type: 'order',
                    price: 0,
                    priceText: '',
                    label: ''
                },
                times: [],
                orders: null,
                allUsers: null,
                userStatus: 'user'
            }
        },
        beforeMount(){
            // Get All Users
            this.getAllUsers()

            // Check Logged In
            if(this.$store.state.currentUser !== null){
                this.isLoggedIn = true
                
                if(this.$store.state.currentUser.uid === 'MxARUElzBNhyyJbdwYkOL55vxxJ2'){
                    this.userStatus = 'admin'
                }
            }
            // Check Location
            if(this.$store.state.location.state !== 'British Columbia'){
                this.pageAllowed = false
            }
        },
        created(){
            this.userProfile = this.getUserProfile

            if(this.$store.state.userProfile !== null && this.$store.state.userProfile !== 'undefined'){
                this.cart.userFirstName = this.$store.state.userProfile.firstName
                this.cart.userPhotoURL = this.$store.state.userProfile.photoURL
            }

            console.log('Default Cart: ', this.cart)

            this.cart.userId = this.$store.state.currentUser.uid
            this.cart.buuz_unit_price = this.foods[0].price
            this.cart.bansh_unit_price = this.foods[1].price
            this.cart.guril_unit_price = this.foods[2].price
        },
        computed: {
            getUserProfile(){
                return this.$store.state.userProfile
            }
        },
        methods: {
            getAllUsers(){
                fb.usersCollection.orderBy('timestamp', 'desc').get().then((snap) => {
                    let allUsersObj = {}
                    snap.forEach((member) => {
                        allUsersObj[member.id] = member.data()
                    })
                    this.allUsers = allUsersObj
                    console.log('All Users: ', this.allUsers)
                }).catch(err => {
                    console.log('Error getting all users: ', err.message)
                })
            },
            increase(id){
                this.cart[id] += 1
                this.getCartTotal()
            },
            decrease(id){
                if(this.cart[id] > 0){
                    this.cart[id] -= 1
                    this.getCartTotal()
                }else{
                    this.cart[id] = 0
                    alert('You have reached minimum amount.')
                }
            },
            getCartTotal(){
                let totalAmount = 0
                
                for(let i = 0; i < this.foods.length; i++){
                    if(this.cart[this.foods[i].id] > 0){
                        totalAmount += this.cart[this.foods[i].id] * this.foods[i].price
                    }
                }
                
                this.cart.total = totalAmount

                if(this.cart.total >= 75){
                    this.cart.delivery = '$0 fee on orders over $75.'
                    this.cart.delivery_fee = 0
                }else{
                    this.cart.delivery = '$5 minimum delivery fee.'
                    this.cart.delivery_fee = 5
                }
            },
            goTo(page){
                this.page = page

                if(page === 2){

                    // Pre Fill Address
                    if(this.$store.state.userProfile.address !== null && typeof(this.$store.state.userProfile.address) !== 'undefined'){
                        console.log('User address: ', this.$store.state.userProfile.address)
                        this.cart.address = this.$store.state.userProfile.address
                        this.cart.addressValid = true
                    }else{
                        console.log('User does not have any address!')
                    }
                    // Pre Fill Phone Number
                    if(this.$store.state.userProfile.phoneNumber !== null && typeof(this.$store.state.userProfile.phoneNumber) !== 'undefined'){
                        console.log('User phone number: ', this.$store.state.userProfile.phoneNumber)
                        this.cart.phoneNumber = this.$store.state.userProfile.phoneNumber
                        this.cart.phoneNumberValid = true
                    }else{
                        console.log('User does not have any phone number!')
                    }

                }else if(page === 3){

                    // Save the address
                    if(this.$store.state.userProfile.address !== null && typeof(this.$store.state.userProfile.address) !== 'undefined'){
                        let updateAddressCheck = false
                        if(this.cart.address.street !== this.$store.state.userProfile.address.street){
                            updateAddressCheck = true
                        }
                        if(this.cart.address.city !== this.$store.state.userProfile.address.city){
                            updateAddressCheck = true
                        }
                        if(this.cart.address.state !== this.$store.state.userProfile.address.state){
                            updateAddressCheck = true
                        }
                        if(this.cart.address.postalCode !== this.$store.state.userProfile.address.postalCode){
                            updateAddressCheck = true
                        }
                        if(updateAddressCheck){
                            this.updateAddress()
                        }
                    }

                }else if(page === 4){

                    // Save Phone Number
                    if(this.$store.state.userProfile.phoneNumber !== null && this.$store.state.userProfile.phoneNumber !== 'undefined'){
                        if(this.cart.phoneNumber !== this.$store.state.userProfile.phoneNumber){
                            this.updatePhoneNumber()    
                        }
                    }

                    // Render time
                    this.renderTimes()
                
                }else if(page === 5){

                    // Save the phone number
                    if(this.$store.state.userProfile.phoneNumber !== null && typeof(this.$store.state.userProfile.phoneNumber) !== 'undefined'){
                        if(this.$store.state.userProfile.phoneNumber !== this.cart.phoneNumber){
                            this.updatePhoneNumber()
                        }
                    }

                }else if(page === 6){

                    // Load Payment
                    if(!this.payment.loadPayment){
                        this.payment.loadPayment = true
                        this.payment.price = this.cart.total + this.cart.delivery_fee
                        this.payment.priceText = '$'+ (this.cart.total + this.cart.delivery_fee)
                        this.payment.label = 'Eats Order'
                    }
            
                // Disable Payment
                }else{
                    this.payment.loadPayment = false
                }
            },
            checkAddress(){
                let isValid = false

                if(this.cart.address.street !== '' && this.cart.address.city !== ''){
                    if(this.cart.address.state === 'BC' && this.cart.address.country === 'CA'){
                        if(this.cart.address.postalCode !== ''){
                            isValid = true
                            this.updateAddress()
                        }
                    }
                }
                
                this.cart.addressValid = isValid
            },
            updateAddress(){
                let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)

                userRef.update({
                    address: this.cart.address
                }).then(() => {
                    console.log('User address updated successfully! ', this.cart.address)
                }).catch(error => {
                    console.error("Error updating user address: ", error)
                })
            },
            checkPhoneNumber(){
                let isValid = false

                if(this.cart.phoneNumber !== ''){
                    if(this.cart.phoneNumber.length >= 10){
                        isValid = true
                    }
                }

                this.cart.phoneNumberValid = isValid
            },
            updatePhoneNumber(){
                let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)

                userRef.update({
                    phoneNumber: this.cart.phoneNumber
                }).then(() => {
                    console.log('User phone number updated successfully! ', this.cart.phoneNumber)
                }).catch(error => {
                    console.error("Error updating user address: ", error)
                })
            },
            renderTimes(){
                let x = {
                    nextSlot: 60,
                    breakTime: [
                        ['11:00', '14:00'], ['16:00', '18:00']
                    ],
                    startTime: '10:00',
                    endTime: '22:00'
                }
                let currentTime = moment()
                let slotTime = moment(x.startTime, "HH:mm")
                let endTime = moment(x.endTime, "HH:mm")
                let isOpen = false
                let tempTimes = []
                
                function isInBreak(slotTime, breakTimes) {
                    return breakTimes.some((br) => {
                        return slotTime >= moment(br[0], "HH:mm") && slotTime < moment(br[1], "HH:mm")
                    })
                }

                while (slotTime < endTime){
                    if (!isInBreak(slotTime, x.breakTime)){

                        if(currentTime.isBefore(slotTime) && currentTime.isBefore(slotTime)){
                        
                            let to = slotTime.add(x.nextSlot, 'minutes').format("h:mm_a")
                            slotTime = slotTime.subtract(x.nextSlot, 'minutes')
                            let to2 = slotTime.add(x.nextSlot, 'minutes').format("h:mm a")
                            slotTime = slotTime.subtract(x.nextSlot, 'minutes')
                            
                            tempTimes.push({
                                id: slotTime.format("h:mm_a") + '-' + to,
                                text: slotTime.format("h:mm a") + ' - ' + to2
                            })
                        }
                    }
                    slotTime = slotTime.add(x.nextSlot, 'minutes')
                }

                this.times = tempTimes
            },
            updateCart(type){
                
                if(type === 'make3kg'){
                    this.cart.delivery = '$0 fee for 3kg and above'
                    this.cart.delivery_fee = 0
                    this.cart.buuz = 3
                    this.getCartTotal()
                }else if(type === 'pay5'){
                    this.cart.delivery = 'Pay $5 minimum order fee'
                    this.cart.delivery_fee = 5
                }
            },
            createNewOrder(){
                let ordersRef = fb.ordersCollection
                
                this.cart.timestamp = fb.firebase.firestore.FieldValue.serverTimestamp()
                this.cart.status = 'Preparing'
                
                ordersRef.add(this.cart).then(docRef => {
                    
                    let orderId = docRef.id
                    let userOrdersRef = fb.usersCollection.doc(this.$store.state.currentUser.uid).collection('orders')

                    console.log('New order added with id: ', orderId)

                    this.cart.orderId = orderId
                    
                    userOrdersRef.doc(orderId).set(this.cart, {merge: true}).then(() => {
                        this.page = 6
                        this.getExistingOrders()
                    }).catch(error => {
                        this.page = 6
                        this.getExistingOrders()
                        console.log('Error adding order to user orders: ', error)
                    })

                }).catch(err => {
                    console.log('Error creating new order: ', err)
                })
            },
            paymentSuccess(e){
                this.createNewOrder()
            },
            getExistingOrders(){

                let ordersRef = fb.ordersCollection
                let currentUserId = this.$store.state.currentUser.uid

                ordersRef.orderBy('timestamp', 'desc').get().then(snap => {
                    
                    let listOfOrders = []

                    snap.forEach((data) => {
                        let orderData = data.data()
                        
                        orderData.id = data.id
                        orderData.userfirstName = this.allUsers[orderData.userId].firstName
                        orderData.userPhoto = this.allUsers[orderData.userId].photoURL

                        if(this.userStatus === 'admin'){
                            listOfOrders.push(orderData)
                        }else if(this.userStatus === 'user'){
                            if(orderData.userId === currentUserId){
                                listOfOrders.push(orderData)
                            }
                        }
                    })

                    console.log('List of orders: ', listOfOrders)

                    this.orders = listOfOrders
                })
            },
            changeOrderStatus(id, status){
                let orderRef = fb.ordersCollection.doc(id)

                orderRef.update({
                    status: status
                }).then(() => {
                    console.log('Successfully updated order status!')
                    this.getExistingOrders()
                }).catch(err => {
                    console.log('Error updating order status: ', err.message)
                })
            },
            toggleTabs(tabId){
                this.tab = tabId
                if(this.tab === 'orders'){
                    this.getExistingOrders()
                }
            }
        },
        filters: {
            removeUnderscores(value){
                if(value){
                    return value.replace(/_/g,' ').replace(/-/g,' - ')
                }
            }
        }
    }
</script>


// WEBPACK FOOTER //
// src/views/Eats/Eats.vue